import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import * as stylex from "@stylexjs/stylex";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";
const styles = {
  trigger: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x166cprz",
    k7Eaqz: "xkgs3ab",
    kZKoxP: "x5esxvw",
    kg3NbH: "xtft4pz",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7 xunrbct",
    kaIpWk: "x16l1o21",
    kWkggS: "x1h1f4i9",
    kMwMTN: "xgwkh9f",
    kGuDYH: "xtu3avg",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  navigation: {
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kzqmXN: "xgd8bvy",
    kZKoxP: "x1fgtraw",
    kaIpWk: "x3kktt9",
    kMwMTN: "x1e3avs9",
    kWkggS: "xjbqb8w x1ki36sz",
    kSiTet: "x1hc1fzr x1bq4at4",
    $$css: true
  },
  day: {
    kzqmXN: "xh8yej3",
    kZKoxP: "x10w6t97",
    kaIpWk: "x3kktt9",
    k9WMMc: "x2b8uid",
    kMwMTN: "x1e3avs9 x176rghm",
    kWkggS: "xjbqb8w x1ki36sz",
    kGuDYH: "x7x6yu3",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  outside: {
    kMwMTN: "x1i6qc9y x176rghm",
    $$css: true
  },
  selected: {
    kWkggS: "xa2s0qy x1qaxybf",
    kMwMTN: "x1sen6cn x1ufzn7k",
    k63SB2: "x1s688f",
    $$css: true
  },
  note: {
    keoZOQ: "x1fp82tx",
    k1K539: "xat24cr",
    kLKAdn: "xfisy6v",
    kg3NbH: "x138t5iz",
    kGuDYH: "x1hqrb6m",
    kMwMTN: "x1i6qc9y",
    $$css: true
  }
};
const dateFor = (value: string) => new Date(`${value}T12:00:00`);
const dayFor = (date: Date) => date.toLocaleDateString("sv-SE");
const labelFor = (value: string) => dateFor(value).toLocaleDateString("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric"
});
function shiftMonth(value: string, offset: number) {
  const date = dateFor(value);
  const last = new Date(date.getFullYear(), date.getMonth() + offset + 1, 0, 12);
  return dayFor(new Date(last.getFullYear(), last.getMonth(), Math.min(date.getDate(), last.getDate()), 12));
}
export default function DatePicker({
  value,
  max,
  label,
  onChange
}: {
  value: string;
  max: string;
  label: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  }, []);
  return <>
      <button ref={trigger} type="button" {...stylex.props(recipes.bareButton, styles.trigger, recipes.focusRing)} aria-label={label} aria-describedby={`${id}-value`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(!open)}>
        <span id={`${id}-value`}>
          {dateFor(value).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric"
        })}
        </span>
        <span aria-hidden="true" className="x3nfvp2 x1i6qc9y">
          <CalendarDays size={14} />
        </span>
      </button>
      {open && createPortal(<Calendar value={value} max={max} label={label} id={id} trigger={trigger} close={close} onChange={onChange} />, document.body)}
    </>;
}
function Calendar({
  value,
  max,
  label,
  id,
  trigger,
  close,
  onChange
}: {
  value: string;
  max: string;
  label: string;
  id: string;
  trigger: RefObject<HTMLButtonElement | null>;
  close: (restoreFocus?: boolean) => void;
  onChange: (value: string) => void;
}) {
  const [activeDay, setActiveDay] = useState(value > max ? max : value);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: 400
  });
  const popup = useRef<HTMLDivElement>(null);
  const focusDay = useRef(true);
  const active = dateFor(activeDay);
  const monthLabel = active.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric"
  });
  const first = new Date(active.getFullYear(), active.getMonth(), 1, 12);
  first.setDate(first.getDate() - (first.getDay() + 6) % 7);
  const days = Array.from({
    length: 42
  }, (_, index) => {
    const date = new Date(first);
    date.setDate(date.getDate() + index);
    return dayFor(date);
  });
  useLayoutEffect(() => {
    if (focusDay.current) {
      popup.current?.querySelector<HTMLButtonElement>(`[data-day="${activeDay}"]`)?.focus();
      focusDay.current = false;
    }
  }, [activeDay]);
  useLayoutEffect(() => {
    const reposition = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const height = popup.current?.offsetHeight ?? 340;
      const below = window.innerHeight - rect.bottom - 14;
      const above = rect.top - 14;
      const flip = below < height && above > below;
      setPosition({
        top: flip ? Math.max(8, rect.top - height - 6) : rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
        maxHeight: Math.max(80, flip ? above : below)
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [trigger]);
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popup.current?.contains(target) && !trigger.current?.contains(target)) close();
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [close, trigger]);
  function moveMonth(offset: number) {
    const next = shiftMonth(activeDay, offset);
    setActiveDay(next > max ? max : next);
  }
  function keyboard(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      // The calendar is portalled to body. Continue from its toolbar trigger,
      // not from the end of the document.
      const controls = Array.from(document.querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex]")).filter(element => element.tabIndex >= 0 && !element.matches(":disabled") && !element.closest('[hidden], [inert], [aria-hidden="true"]') && !popup.current?.contains(element) && getComputedStyle(element).display !== "none" && getComputedStyle(element).visibility !== "hidden");
      const index = controls.indexOf(trigger.current!);
      close();
      (controls[index + (event.shiftKey ? -1 : 1)] ?? trigger.current)?.focus();
      return;
    }
    if (!(event.target instanceof HTMLElement) || !event.target.dataset.day) return;
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -(active.getDay() + 6) % 7,
      End: 6 - (active.getDay() + 6) % 7
    };
    let next: string;
    if (event.key === "PageUp" || event.key === "PageDown") next = shiftMonth(activeDay, (event.key === "PageUp" ? -1 : 1) * (event.shiftKey ? 12 : 1));else if (offsets[event.key] !== undefined) {
      const date = new Date(active);
      date.setDate(date.getDate() + offsets[event.key]!);
      next = dayFor(date);
    } else return;
    event.preventDefault();
    focusDay.current = true;
    setActiveDay(next > max ? max : next);
  }
  return <div ref={popup} id={id} role="dialog" aria-label={`${label} calendar`} className={{
    className: "xixxii4 x11uqc5h xbu5dzu x1usz39j xw1tjjd x1h1f4i9 xmkeg23 x1y0btm7 xzbo3x7 xhrzdqn x1n1ksyb"
  }.className} style={position} onKeyDown={keyboard} onBlur={event => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget) && event.relatedTarget !== trigger.current) close();
  }}>
      <div className="x78zum5 x6s0dn4 xsuxsxl x105h2rl">
        <span aria-live="polite" className="x98rzlu x10j8lut xtu3avg x1s688f xgwkh9f">
          {monthLabel}
        </span>
        <button type="button" {...stylex.props(recipes.bareButton, styles.navigation, recipes.focusRing)} aria-label="Previous month" onClick={() => moveMonth(-1)}>
          <ChevronLeft size={15} />
        </button>
        <button type="button" {...stylex.props(recipes.bareButton, styles.navigation, recipes.focusRing)} aria-label="Next month" disabled={activeDay.slice(0, 7) >= max.slice(0, 7)} onClick={() => moveMonth(1)}>
          <ChevronRight size={15} />
        </button>
      </div>
      <div role="grid" aria-label={monthLabel}>
        <div role="row" className="xrvj5dj x1gj6vdr x195vfkc">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map(day => <span role="columnheader" key={day} className="x67pdoy x1f9svqt x2b8uid x1k6wstc xk50ysn x1i6qc9y">
              {day}
            </span>)}
        </div>
        {Array.from({
        length: 6
      }, (_, week) => <div role="row" className="xrvj5dj x1gj6vdr x195vfkc" key={days[week * 7]}>
            {days.slice(week * 7, week * 7 + 7).map(day => <div role="gridcell" key={day} aria-selected={day === value}>
                <button type="button" data-day={day} aria-label={labelFor(day)} disabled={day > max} tabIndex={day === activeDay ? 0 : -1} {...stylex.props(recipes.bareButton, styles.day, recipes.focusRing, day.slice(0, 7) !== activeDay.slice(0, 7) && styles.outside, day === value && styles.selected)} onClick={() => {
            onChange(day);
            close(true);
          }}>
                  {dateFor(day).getDate()}
                </button>
              </div>)}
          </div>)}
      </div>
      <p {...stylex.props(styles.note, recipes.hairlineTopSoft)}>
        Completed days only · through yesterday
      </p>
    </div>;
}